import React, { useState } from 'react';
import { Routes, Route, useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import Badge from '../components/common/Badge';
import Table from '../components/common/Table';
import Modal from '../components/common/Modal';
import Input from '../components/common/Input';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import { CheckCircle, XCircle, Download, Search } from 'lucide-react';
import { formatDate, formatCurrency, formatDateTime, delay } from '../utils/helpers';
import { generateJustificanteAutorizacionPDF } from '../services/pdfService';
import toast from 'react-hot-toast';

const AveriasListPage = () => {
  const { mockData } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('todas');
  const [searchTerm, setSearchTerm] = useState('');

  const averias = React.useMemo(() => {
    let filtered = mockData?.averias || [];

    if (activeTab !== 'todas') {
      const tabFilters = {
        'en_curso': ['pendiente', 'en_curso'],
        'pendientes': ['pendiente'],
        'autorizadas': ['autorizada'],
        'finalizadas': ['finalizada', 'facturada'],
        'rechazadas': ['rechazada']
      };
      filtered = filtered.filter(a => tabFilters[activeTab]?.includes(a.estado));
    }

    if (searchTerm) {
      filtered = filtered.filter(a =>
        a.numeroAveria.toLowerCase().includes(searchTerm.toLowerCase()) ||
        a.tipo.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return filtered;
  }, [mockData, activeTab, searchTerm]);

  const tabs = [
    { id: 'todas', label: 'Todas', count: mockData?.averias.length || 0 },
    { id: 'en_curso', label: 'En Curso', count: mockData?.averias.filter(a => ['pendiente', 'en_curso'].includes(a.estado)).length || 0 },
    { id: 'pendientes', label: 'Pendientes', count: mockData?.averias.filter(a => a.estado === 'pendiente').length || 0 },
    { id: 'autorizadas', label: 'Autorizadas', count: mockData?.averias.filter(a => a.estado === 'autorizada').length || 0 },
    { id: 'finalizadas', label: 'Finalizadas', count: mockData?.averias.filter(a => ['finalizada', 'facturada'].includes(a.estado)).length || 0 }
  ];

  const getEstadoBadge = (estado) => {
    const variants = {
      'pendiente': 'warning',
      'en_curso': 'info',
      'autorizada': 'success',
      'rechazada': 'danger',
      'finalizada': 'default',
      'facturada': 'primary'
    };
    return <Badge variant={variants[estado]}>{estado.toUpperCase().replace('_', ' ')}</Badge>;
  };

  const columns = [
    {
      header: 'N Averia',
      cell: (row) => (
        <span className="font-mono text-sm font-semibold text-gray-900">{row.numeroAveria}</span>
      )
    },
    {
      header: 'Tipo',
      accessor: 'tipo'
    },
    {
      header: 'Fecha Reporte',
      cell: (row) => (
        <span className="text-sm text-gray-500">{formatDate(row.fechaReporte)}</span>
      )
    },
    {
      header: 'Estado',
      cell: (row) => getEstadoBadge(row.estado)
    },
    {
      header: 'Cobertura',
      cell: (row) => (
        <Badge variant={row.cubierta ? 'success' : 'danger'}>
          {row.cubierta ? 'CUBIERTA' : 'NO CUBIERTA'}
        </Badge>
      )
    },
    {
      header: 'Coste',
      cell: (row) => (
        <span className="font-semibold text-gray-900">{formatCurrency(row.costeEstimado)}</span>
      )
    }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Averias</h1>
        <p className="text-gray-500 mt-1">Gestion y autorizacion de averias</p>
      </div>

      <Card>
        <div className="border-b border-gray-200 mb-6">
          <div className="flex space-x-6 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`pb-3 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-primary-700 text-primary-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
                <span className={`ml-2 px-2 py-0.5 rounded-md text-xs ${
                  activeTab === tab.id
                    ? 'bg-primary-50 text-primary-700'
                    : 'bg-gray-100 text-gray-500'
                }`}>
                  {tab.count}
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="mb-6">
          <Input
            placeholder="Buscar por numero de averia o tipo..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            icon={Search}
          />
        </div>

        <Table
          columns={columns}
          data={averias}
          onRowClick={(row) => navigate(`/averias/${row.id}`)}
        />
      </Card>
    </div>
  );
};

const AveriaDetallePage = () => {
  const { id } = useParams();
  const { mockData, updateMockData, addAuditLog, user } = useAuth();
  const { addNotification } = useNotifications();
  const navigate = useNavigate();

  const [showAutorizarModal, setShowAutorizarModal] = useState(false);
  const [showRechazarModal, setShowRechazarModal] = useState(false);
  const [aceptaTerminos, setAceptaTerminos] = useState(false);
  const [loading, setLoading] = useState(false);

  const averia = mockData?.averias.find(a => a.id === id);
  const garantia = mockData?.garantias.find(g => g.id === averia?.garantiaId);

  if (!averia || !garantia) {
    return (
      <div className="space-y-6">
        <Card>
          <p className="text-center text-gray-500 py-8">Averia no encontrada</p>
        </Card>
      </div>
    );
  }

  const handleAutorizar = async () => {
    if (!aceptaTerminos) {
      toast.error('Debe aceptar los terminos para continuar');
      return;
    }

    setLoading(true);
    await delay(1500);

    const evidenciaLegal = {
      timestamp: new Date().toISOString(),
      ip: '192.168.1.' + Math.floor(Math.random() * 255),
      userAgent: navigator.userAgent,
      hash: Array.from({ length: 64 }, () => Math.floor(Math.random() * 16).toString(16)).join('')
    };

    const averiaActualizada = {
      ...averia,
      estado: 'autorizada',
      fechaAutorizacion: new Date().toISOString(),
      autorizadoPor: user.email,
      evidenciaLegal
    };

    const updatedAverias = mockData.averias.map(a =>
      a.id === id ? averiaActualizada : a
    );

    updateMockData({ averias: updatedAverias });
    addAuditLog('autorizar_averia', 'averia', id, `Averia ${averia.numeroAveria} autorizada`);
    addNotification('success', 'Averia Autorizada', `La averia ${averia.numeroAveria} ha sido autorizada`);

    toast.success('Averia autorizada correctamente');
    setLoading(false);
    setShowAutorizarModal(false);

    generateJustificanteAutorizacionPDF(averiaActualizada, garantia);

    setTimeout(() => {
      navigate('/averias');
    }, 1000);
  };

  const handleRechazar = async () => {
    setLoading(true);
    await delay(1000);

    const averiaActualizada = {
      ...averia,
      estado: 'rechazada'
    };

    const updatedAverias = mockData.averias.map(a =>
      a.id === id ? averiaActualizada : a
    );

    updateMockData({ averias: updatedAverias });
    addAuditLog('rechazar_averia', 'averia', id, `Averia ${averia.numeroAveria} rechazada`);
    addNotification('warning', 'Averia Rechazada', `La averia ${averia.numeroAveria} ha sido rechazada`);

    toast.success('Averia rechazada');
    setLoading(false);
    setShowRechazarModal(false);

    setTimeout(() => {
      navigate('/averias');
    }, 1000);
  };

  const InfoField = ({ label, children }) => (
    <div>
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">{label}</p>
      <div className="text-sm font-semibold text-gray-900">{children}</div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Detalle de Averia</h1>
          <p className="text-gray-500 mt-1">{averia.numeroAveria}</p>
        </div>
        <Button variant="outline" onClick={() => navigate('/averias')}>
          Volver
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card title="Informacion de la Averia">
            <div className="grid grid-cols-2 gap-5">
              <InfoField label="Numero de Averia">{averia.numeroAveria}</InfoField>
              <InfoField label="Tipo">{averia.tipo}</InfoField>
              <InfoField label="Fecha Reporte">{formatDate(averia.fechaReporte)}</InfoField>
              <InfoField label="Estado">
                <div className="mt-0.5">
                  <Badge variant={averia.estado === 'autorizada' ? 'success' : averia.estado === 'rechazada' ? 'danger' : 'warning'}>
                    {averia.estado.toUpperCase()}
                  </Badge>
                </div>
              </InfoField>
              <div className="col-span-2">
                <InfoField label="Descripcion">{averia.descripcion}</InfoField>
              </div>
              <InfoField label="Taller">{averia.taller}</InfoField>
              <InfoField label="Coste Estimado">
                <span className="text-base">{formatCurrency(averia.costeEstimado)}</span>
              </InfoField>
            </div>
          </Card>

          <Card title="Evaluacion de Cobertura">
            <div className="flex items-start space-x-4">
              {averia.cubierta ? (
                <>
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-success-50 rounded-lg flex items-center justify-center border border-success-200">
                      <CheckCircle className="w-5 h-5 text-success-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-success-700 mb-1">AVERIA CUBIERTA</h3>
                    <p className="text-sm text-gray-500">
                      Esta averia esta cubierta por la garantia del vehiculo segun el nivel de cobertura contratado.
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-danger-50 rounded-lg flex items-center justify-center border border-danger-200">
                      <XCircle className="w-5 h-5 text-danger-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-danger-700 mb-1">AVERIA NO CUBIERTA</h3>
                    <p className="text-sm text-gray-500 mb-2">
                      Esta averia no esta cubierta por la garantia.
                    </p>
                    <p className="text-xs text-gray-500">
                      <span className="font-semibold text-gray-700">Motivo:</span> {averia.motivoRechazo}
                    </p>
                  </div>
                </>
              )}
            </div>
          </Card>

          <Card title="Vehiculo Afectado">
            <div className="grid grid-cols-2 gap-5">
              <InfoField label="Matricula">{garantia.vehiculo.matricula}</InfoField>
              <InfoField label="Marca / Modelo">{garantia.vehiculo.marca} {garantia.vehiculo.modelo}</InfoField>
              <InfoField label="Propietario">{garantia.propietario.nombre}</InfoField>
              <InfoField label="N Garantia">{garantia.numeroGarantia}</InfoField>
            </div>
          </Card>

          {averia.evidenciaLegal && (
            <Card title="Evidencia Legal de Autorizacion">
              <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg space-y-3">
                <InfoField label="Timestamp">
                  <span className="font-mono text-sm">{formatDateTime(averia.evidenciaLegal.timestamp)}</span>
                </InfoField>
                <InfoField label="Autorizado por">{averia.autorizadoPor}</InfoField>
                <InfoField label="IP">
                  <span className="font-mono text-sm">{averia.evidenciaLegal.ip}</span>
                </InfoField>
                <InfoField label="Hash de Verificacion">
                  <span className="font-mono text-xs break-all text-gray-500">{averia.evidenciaLegal.hash}</span>
                </InfoField>
                <div className="pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    icon={Download}
                    onClick={() => generateJustificanteAutorizacionPDF(averia, garantia)}
                  >
                    Descargar Justificante
                  </Button>
                </div>
              </div>
            </Card>
          )}
        </div>

        <div className="space-y-6">
          {averia.estado === 'pendiente' && averia.cubierta && (
            <Card title="Acciones">
              <div className="space-y-3">
                <Button
                  variant="success"
                  fullWidth
                  icon={CheckCircle}
                  onClick={() => setShowAutorizarModal(true)}
                >
                  AUTORIZAR REPARACION
                </Button>
                <Button
                  variant="danger"
                  fullWidth
                  icon={XCircle}
                  onClick={() => setShowRechazarModal(true)}
                >
                  RECHAZAR
                </Button>
              </div>
            </Card>
          )}

          <Card title="Timeline">
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-primary-700 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">Averia reportada</p>
                  <p className="text-xs text-gray-500">{formatDateTime(averia.fechaReporte)}</p>
                </div>
              </div>
              {averia.fechaAutorizacion && (
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-success-600 rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Averia autorizada</p>
                    <p className="text-xs text-gray-500">{formatDateTime(averia.fechaAutorizacion)}</p>
                  </div>
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>

      <Modal
        isOpen={showAutorizarModal}
        onClose={() => setShowAutorizarModal(false)}
        title="Autorizar Reparacion"
        footer={
          <div className="flex justify-end space-x-3">
            <Button variant="outline" onClick={() => setShowAutorizarModal(false)}>
              Cancelar
            </Button>
            <Button
              variant="success"
              onClick={handleAutorizar}
              loading={loading}
              disabled={!aceptaTerminos}
            >
              Confirmar Autorizacion
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div className="bg-warning-50 border border-warning-200 rounded-lg p-4">
            <p className="text-sm text-warning-700">
              <span className="font-semibold">Importante:</span> Al autorizar esta reparacion, esta confirmando que la averia esta cubierta
              por la garantia y que se procedera con la reparacion. Esta accion quedara registrada legalmente.
            </p>
          </div>

          <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-500">Numero de Averia:</span>
              <span className="text-sm font-semibold text-gray-900">{averia.numeroAveria}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-500">Coste Estimado:</span>
              <span className="text-sm font-semibold text-gray-900">{formatCurrency(averia.costeEstimado)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-500">Taller:</span>
              <span className="text-sm font-semibold text-gray-900">{averia.taller}</span>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-4">
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={aceptaTerminos}
                onChange={(e) => setAceptaTerminos(e.target.checked)}
                className="mt-1 w-4 h-4 text-primary-700 border-gray-300 rounded focus:ring-primary-500"
              />
              <span className="text-sm text-gray-500">
                He leido y acepto los terminos de autorizacion. Confirmo que esta averia esta cubierta
                por la garantia y autorizo su reparacion. Entiendo que esta accion quedara registrada
                con evidencia legal (timestamp, IP, hash de verificacion).
              </span>
            </label>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showRechazarModal}
        onClose={() => setShowRechazarModal(false)}
        title="Rechazar Averia"
        footer={
          <div className="flex justify-end space-x-3">
            <Button variant="outline" onClick={() => setShowRechazarModal(false)}>
              Cancelar
            </Button>
            <Button
              variant="danger"
              onClick={handleRechazar}
              loading={loading}
            >
              Confirmar Rechazo
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-500">
            Esta seguro de que desea rechazar esta averia? Esta accion no se puede deshacer.
          </p>
        </div>
      </Modal>
    </div>
  );
};

const AveriasPage = () => {
  return (
    <Layout>
      <Routes>
        <Route index element={<AveriasListPage />} />
        <Route path=":id" element={<AveriaDetallePage />} />
      </Routes>
    </Layout>
  );
};

export default AveriasPage;
